import {useQuery, useQueryClient} from '@tanstack/react-query'

import {GetImportStatusResponse} from 'interfaces'
import {SettingsService} from 'services/api'

// React Query v4's built-in refetchInterval type operates on the raw query
// data (before `select`), so it doesn't know the transformed return type.
// This custom type lets callers use the post-select `GetImportStatusResponse`
// directly in the callback signature.
type RefetchIntervalFn = (
  data: GetImportStatusResponse | undefined,
) => number | false

interface GetImportStatusQueryOptions {
  enabled?: boolean
  refetchInterval?: number | false | RefetchIntervalFn
}

export const GET_IMPORT_STATUS_QUERY_KEY = 'getImportStatus'

export const useGetImportStatusQuery = ({
  enabled = true,
  refetchInterval,
}: GetImportStatusQueryOptions) => {
  return useQuery({
    queryKey: [GET_IMPORT_STATUS_QUERY_KEY],
    queryFn: () => SettingsService.getImportStatus(),
    enabled,
    select: ({data}) => data,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchInterval,
    refetchOnWindowFocus: 'always',
  })
}

// Read-only hook that returns import status data from the React Query cache
// without triggering any network requests. Use this in components that only
// need to consume the current value (e.g. to gate UI) but should not be
// responsible for fetching or polling.
export const useImportStatusCache = (): GetImportStatusResponse | undefined => {
  const queryClient = useQueryClient()
  return queryClient.getQueryData<GetImportStatusResponse>([
    GET_IMPORT_STATUS_QUERY_KEY,
  ])
}
